import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { queryKeys } from '@/lib/api/query-keys';
import * as authApi from '../api';
import type { User } from '../types';
import type {
  LoginInput,
  RegisterInput,
  ForgotPasswordInput,
} from '../schemas';

// ---------------------------------------------------------------------------
// Current user query
// ---------------------------------------------------------------------------

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated' | 'error';

export function useAuth() {
  const query = useQuery({
    queryKey: queryKeys.auth.me(),
    queryFn: ({ signal }) => authApi.getMe(signal),
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
  });

  const user: User | null = query.data?.data ?? null;

  let status: AuthStatus;
  if (query.isLoading) {
    status = 'loading';
  } else if (query.isError) {
    // Distinguish 401 (not logged in) from network errors
    const err = query.error;
    const is401 =
      err && 'status' in err && (err as { status: number }).status === 401;
    status = is401 ? 'unauthenticated' : 'error';
  } else if (user) {
    status = 'authenticated';
  } else {
    status = 'unauthenticated';
  }

  return {
    user,
    status,
    isLoading: status === 'loading',
    isAuthenticated: status === 'authenticated',
    isAdmin: user?.role === 'ADMIN',
    error: query.error,
    refetch: query.refetch,
  };
}

// ---------------------------------------------------------------------------
// Login
// ---------------------------------------------------------------------------

export function useLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: LoginInput) => authApi.login(data),
    retry: false,       // Never auto-retry login
    onSuccess: (res) => {
      queryClient.setQueryData(queryKeys.auth.me(), res);
    },
  });
}

// ---------------------------------------------------------------------------
// Register
// ---------------------------------------------------------------------------

export function useRegister() {
  return useMutation({
    mutationFn: (data: RegisterInput) => authApi.register(data),
    retry: false,
  });
}

// ---------------------------------------------------------------------------
// Logout
// ---------------------------------------------------------------------------

export function useLogout() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: () => authApi.logout(),
    retry: false,
    onSuccess: () => {
      // Clear ALL cached data to prevent data leaking between sessions
      queryClient.clear();
      navigate('/login', { replace: true });
    },
  });
}

// ---------------------------------------------------------------------------
// Logout all sessions
// ---------------------------------------------------------------------------

export function useLogoutAll() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: () => authApi.logoutAll(),
    retry: false,
    onSuccess: () => {
      queryClient.clear();
      navigate('/login', { replace: true });
    },
  });
}

// ---------------------------------------------------------------------------
// Forgot password
// ---------------------------------------------------------------------------

export function useForgotPassword() {
  return useMutation({
    mutationFn: (data: ForgotPasswordInput) => authApi.forgotPassword(data),
    retry: false,
  });
}

// ---------------------------------------------------------------------------
// Reset password
// ---------------------------------------------------------------------------

export function useResetPassword() {
  return useMutation({
    mutationFn: (data: { token: string; password: string }) =>
      authApi.resetPassword(data),
    retry: false,
  });
}

// ---------------------------------------------------------------------------
// Verify email
// ---------------------------------------------------------------------------

export function useVerifyEmail() {
  return useMutation({
    mutationFn: (token: string) => authApi.verifyEmail(token),
    retry: false,
  });
}

// ---------------------------------------------------------------------------
// Resend verification
// ---------------------------------------------------------------------------

export function useResendVerification() {
  return useMutation({
    mutationFn: (email: string) => authApi.resendVerification(email),
    retry: false,
  });
}
